import { ClerkProvider } from "@clerk/clerk-react";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { routeTree } from "@/routeTree.gen.ts";
import "./i18n/index.ts";
import { useAuth } from "@clerk/clerk-react";
import { LanguageProvider } from "@/hooks/useLanguage.tsx";
import { ThemeProvider } from "./hooks/useTheme.tsx";
import "./styles.css";
import { ptBR } from "@clerk/localizations";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { setGlobalGetToken } from "@/api/api_client.ts";
import reportWebVitals from "./reportWebVitals.ts";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
	throw new Error("Add your Clerk Publishable Key to the .env.local file");
}

const queryClient = new QueryClient();

// Create a new router instance
const router = createRouter({
	routeTree,
	context: {
		queryClient,
		// biome-ignore lint/style/noNonNullAssertion: reference from docs https://tanstack.com/router/latest/docs/framework/react/guide/authenticated-routes
		userId: undefined!,
		// biome-ignore lint/style/noNonNullAssertion: reference from docs https://tanstack.com/router/latest/docs/framework/react/guide/authenticated-routes
		get_token: undefined!,
	},
	defaultPreload: "intent",
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

function SetupQueryClient() {
	const { userId, getToken, isLoaded } = useAuth();

	// Set up global token function for API client interceptors
	useEffect(() => {
		if (isLoaded && getToken) {
			setGlobalGetToken(getToken);
		}
	}, [isLoaded, getToken]);

	if (!isLoaded) {
		return (
			<div className="min-h-screen bg-base-100 flex items-center justify-center">
				<div className="loading loading-spinner loading-lg text-primary" />
			</div>
		);
	}

	return (
		<QueryClientProvider client={queryClient}>
			<RouterProvider
				router={router}
				context={{
					userId,
					get_token: getToken,
				}}
			/>
			<Toaster
				position="top-right"
				expand={true}
				richColors={true}
				closeButton={true}
				duration={4000}
				toastOptions={{
					className: "!bg-base-100 !text-base-content !border-base-300",
					style: {
						backgroundColor: "hsl(var(--b1))",
						color: "hsl(var(--bc))",
						border: "1px solid hsl(var(--b3))",
					},
				}}
			/>
		</QueryClientProvider>
	);
}

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);

	root.render(
		<StrictMode>
			<ClerkProvider
				localization={ptBR}
				publishableKey={PUBLISHABLE_KEY}
				afterSignOutUrl="/"
				signInFallbackRedirectUrl="/app"
				signUpFallbackRedirectUrl="/app"
			>
				<LanguageProvider>
					<ThemeProvider>
						<SetupQueryClient />
					</ThemeProvider>
				</LanguageProvider>
			</ClerkProvider>
		</StrictMode>,
	);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
